/* The exercise table — KANON `Cwiczenia` 17.09.2026, variant e.
 *
 * A CSS grid carrying ARIA table roles, for the reason `client-roster`'s table is one: the column
 * labels are what tell PARTIE from SPRZĘT for anyone not looking at the screen, and a bare grid of
 * `<div>`s cannot associate a label with the cell under it. A real `<table>` cannot hold these
 * tracks — under `table-layout: fixed` a `<col>` width written as `minmax()` is read as `auto` and
 * the browser splits the flexible columns equally.
 *
 * Every text leaf STATES its line-height. `base.css` puts `--lh-relaxed` on `body`, and an unstated
 * leaf renders about 2px taller than the specimen, which drifts the whole 52px row. */

/* The scroller. The keyboard's way in, so it is a named region with its own tab stop. */
.scroller {
  --sb-size: 10px;
  --sb-radius: var(--radius-xs);

  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
}

.scroller:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The grid's own floor. Below it the table scrolls sideways INSIDE the card rather than letting the
 * columns collapse — a clip with no scroll path is the one outcome `CLAUDE.md` calls worse than
 * either. 240 + 56 + 220 + 120 + 56 tracks, four 20px gaps, 22px of inset each side = 816. */
.table {
  display: flex;
  flex-direction: column;
  min-width: 816px;
}

/* The five tracks, verbatim from the specimen's `.le-head, .le-row`. */
.gridRow {
  display: grid;
  grid-template-columns:
    minmax(240px, 1.5fr) 56px minmax(220px, 1.2fr) 120px 56px;
  gap: var(--space-0) var(--space-20px);
  align-items: center;
  padding: var(--space-0) var(--space-22px);
}

/* Without the preview panel the table takes the width, and the canon widens every track rather than
 * letting the flexible two absorb all of it — the muscle column then fits a THIRD pill, which is
 * the point of hiding the panel. Floor: 300 + 64 + 280 + 150 + 72 + four 24px gaps + 44 = 1006. */
.wide .table {
  min-width: 1006px;
}

.wide .gridRow {
  grid-template-columns:
    minmax(300px, 1.6fr) 64px minmax(280px, 1.3fr) 150px 72px;
  column-gap: var(--space-6);
}

/* With the „Tagi" column — the tag boards' `F2-lista` `.g6`, verbatim, between SPRZĘT and AUTOR.
 * Floor: 220 + 56 + 170 + 100 + 150 + 56 + five 20px gaps + 44 = 896. */
.withTags .table {
  min-width: 896px;
}

.withTags .gridRow {
  grid-template-columns:
    minmax(220px, 1.4fr) 56px minmax(170px, 1fr) 100px minmax(150px, 0.9fr) 56px;
}

/* The wide cut with tags: two muscle pills rather than three, so it still fits the card at 1280,
 * where the panel is dropped. Floor: 260 + 64 + 220 + 130 + 160 + 72 + five 24px gaps + 44 =
 * 1070. */
.wide.withTags .table {
  min-width: 1070px;
}

.wide.withTags .gridRow {
  grid-template-columns:
    minmax(260px, 1.5fr) 64px minmax(220px, 1.2fr) 130px minmax(160px, 1fr) 72px;
}

.headGroup {
  position: sticky;
  top: var(--space-0);
  z-index: 1;
}

/* The head sits on the inset surface and the rows on the card's, so a 3px lip of card colour above
 * it hides the sliver of row that shows through while the scroller is between two positions. The
 * specimen draws the same `::before` for the same reason. */
.headRow {
  position: relative;
  flex: none;
  height: 38px;
  background: var(--bg-inset);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headRow::before {
  position: absolute;
  top: -3px;
  right: var(--space-0);
  left: var(--space-0);
  height: 3px;
  content: '';
  background: var(--bg-card);
}

.th {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  overflow: hidden;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-group);
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;

  /* `--text-2`, not `--text-3`: measured at 2.86:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
}

/* TYP and AUTOR are centred columns: the glyph and the circle under them are marks rather than
 * text runs, and a left-aligned label over a centred mark reads as a misalignment. */
.thCentred {
  justify-content: center;
}

.thButton {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  max-width: 100%;
  padding: var(--space-0);
  font: inherit;
  letter-spacing: inherit;
  color: inherit;
  text-transform: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.thButton:hover {
  color: var(--text-2);
}

.thButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The column the table is ordered by. Ink is one channel; the caret beside it is the second, and the
 * head's `aria-sort` is the third. */
.thActive {
  color: var(--text);
}

.thIcon {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
}

.thCaret {
  flex: none;
  width: 9px;
  height: 9px;
}

/* Drawn pointing DOWN; ascending turns it over. One drawing, one transform. */
.thCaretUp {
  transform: rotate(180deg);
}

/* The rows' own box.
 *
 * It stands at the height of the WHOLE list whether or not a row is mounted — the table is virtual
 * (see `ExerciseTable`'s header), so the scrollbar and the scroll position have to go on describing
 * 479 rows while the DOM holds a few dozen. The height is the virtualizer's `getTotalSize()` and is
 * written inline, because it is data rather than a decision. */
.rows {
  position: relative;
  flex: none;
  width: 100%;
}

/* Every row is placed by the virtualizer, with a transform rather than a `top`: thirty rows are
 * re-placed on every scroll frame, and that is the compositor's work rather than the layout
 * engine's. The transform also makes the row the containing block its selection bar was already
 * positioned against. */
.row {
  position: absolute;
  top: var(--space-0);
  left: var(--space-0);
  width: 100%;
  height: var(--h-ex-table-row);
  border-bottom: var(--border-hairline) solid var(--border);

  /* The whole row opens the card — see `ExerciseTable`'s own header for why the handler is here and
   * the focusable control is the name inside it. */
  cursor: pointer;
}

.row:hover {
  background: var(--hover-tint);
}

/* The selection: the tint plus a 2px INK bar. Never the accent — on this screen ember marks the
 * header's `+` and nothing else. An absolutely positioned bar rather than an inset `box-shadow`,
 * because stylelint accepts only a token there and an ink bar is geometry rather than elevation. */
.rowSelected {
  background: var(--hover-tint);
}

.selectionBar {
  position: absolute;
  top: var(--space-0);
  left: var(--space-0);
  width: 2px;
  height: 100%;
  background: var(--text);
}

.td {
  min-width: 0;
  overflow: hidden;
  font-weight: var(--fw-regular);
  text-align: left;
}

.tdCentred {
  display: flex;
  justify-content: center;
}

/* ── The name cell ───────────────────────────────────────────────────────────────────────── */

.nameCell {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-width: 0;
}

/* 44 x 33 — the canon's own still. GEOMETRY only — the frame's treatment is `exerciseStill.module.css`'s, and nothing is declared on
 * both sides. See that file's header. */
.still {
  width: 44px;
  height: 33px;
  border-radius: var(--radius-xs);
}

/* The row's OPENER as well as its name — a real `<button>`, so an arrow lands on something with a
 * role and a name rather than on a `role="row"` that silently answers Enter. It is drawn as plain
 * text: the whole row is the target, the hover tint and the selection bar already say so, and a
 * button treatment on four hundred rows would be four hundred controls shouting at once. */
.name {
  min-width: 0;
  padding: var(--space-0);
  overflow: hidden;
  font-family: var(--font-body);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  line-height: var(--lh-name);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  cursor: pointer;
  background: none;
  border: none;
}

/* Board D's row status, „Do uzupełnienia": neutral words after the name, kept whole while the
 * name gives way. */
.review {
  flex: none;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
}

/* The ring is drawn around the whole LINE rather than around the words: the arrows move between
 * rows and the row is what the trainer is on. `:has()` puts it on the row while keeping focus on
 * the control that actually has it, so the ring appears exactly where focus is.
 *
 * INSIDE the row — an outset ring on a 52px row whose neighbour is one hairline away would draw
 * over the row above it. `--outline-inset` is the app's own inward offset. */
.name:focus-visible {
  outline: none;
}

.row:has(.name:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* ── The type cell ───────────────────────────────────────────────────────────────────────── */

/* A 30px wash square carrying the type's own glyph in the type's own hue. The ONLY colour in a row:
 * the pills, the thumbs and the author circle are all neutral. */
.typeSquare {
  display: grid;
  flex: none;
  place-items: center;
  width: 30px;
  height: 30px;
  color: var(--type-ink);
  background: var(--type-wash);
  border-radius: var(--radius-mark);
}

.typeGlyph {
  display: block;
  width: 17px;
  height: 17px;
}

/* ── The muscles cell ────────────────────────────────────────────────────────────────────── */

.pills {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.pill {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 22px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

/* A secondary muscle is the same fact in a weaker treatment, not a different one. */
.pillSecondary {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
}

.more {
  flex: none;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* ── The equipment cell ──────────────────────────────────────────────────────────────────── */

.thumbs {
  display: flex;
  align-items: center;
}

/* Overlapping discs, first on top. The 6px overlap is the specimen's. */
.thumb {
  flex: none;
  width: 26px;
  height: 26px;
  margin-left: var(--space-minus-6px);
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-pill);
}

.thumb:first-child {
  margin-left: var(--space-0);
}

.thumbPhoto {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--r-pill);
}

/* A piece the catalogue names no photograph for. Its own initial, on the same disc, so a movement
 * with a picture and one without stand on the same line and nothing moves when one 404s. */
.thumbLetter {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-size: var(--fs-goal-lab);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: var(--bg-inset);
}

.thumbsMore {
  margin-left: var(--space-6px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* ── The author cell ─────────────────────────────────────────────────────────────────────── */

/* An INK circle carrying the MoveBuddy mark masked in the background colour — owner decision D5:
 * the original mark, never a letter, and the circle is ink rather than the accent. */
.authorLibrary {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  background: var(--text);
  border-radius: var(--r-pill);
}

.authorMark {
  /* The mark is a HOLE in the ink disc rather than ink on it, so the mask paints the background.
   * `brandMark` reads this and states the property itself — see its header for why the override is
   * a variable rather than a second `background-color`. */
  --mark-ink: var(--bg);

  display: block;
  width: 20px;
  height: 20px;
}

/* The trainer's own face or initials, at the same 28px, so the two circles sit on one line. */
.authorOwn {
  --sz-avatar: 28px;
}
